import { useState } from 'react'

import { IconSearch, IconSelector } from '@tabler/icons-react'
import { useNavigate } from '@tanstack/react-router'

import { useUserAccountContextMutation } from '@/api/v1/auth/get-user-account-context'
import { AccountLogo } from '@/components/common/account-logo'
import { CreateAccountIcon } from '@/components/common/create-icon'
import {
  ResponsiveDialog,
  ResponsiveDialogBody,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from '@/components/common/responsive-dialog'
import { SignaterLogo } from '@/components/common/signater-logo'
import { SignaterRectangleLogo } from '@/components/common/signater-rectangle-logo'
import { Input } from '@/components/sui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/sui/popover'
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/sui/sidebar'
import { useComponentTranslation } from '@/hooks/use-component-translation'
import { LOCAL_STORAGE_KEYS } from '@/lib/local-storage-keys'
import { logger } from '@/lib/logger'
import { cn, getRedirectUrl } from '@/lib/utils'
import { useContextStore } from '@/stores/context'
import { useLanguageStore } from '@/stores/language'
import { useManagedAccountStore } from '@/stores/managed-account'
import { useSandboxStore } from '@/stores/sandbox'
import { useThemeStore } from '@/stores/theme'
import { useWhiteLabelStore } from '@/stores/white-label'

import locales from './locales'

// Shared by the interactive trigger and the static (single-account) header so
// the brand logo occupies exactly the same space in every state; only the
// hover background, cursor and chevron differ.
const headerContainerClasses =
  'flex h-12 w-full items-center gap-2 overflow-hidden rounded-md p-2 transition-[width,height,padding,border-radius] duration-200 ease-linear group-data-[collapsible=icon]:size-8! group-data-[collapsible=icon]:gap-0 group-data-[collapsible=icon]:p-0! group-data-[collapsible=icon]:rounded-none!'

interface AccountSwitcherProps {
  /** Opens the account-creation flow. Owned by the parent because on mobile
   *  the sidebar is a sheet that unmounts this component when it closes. */
  onCreateAccount: () => void
}

export function AccountSwitcher({ onCreateAccount }: AccountSwitcherProps) {
  const { t } = useComponentTranslation(locales)
  const { isMobile, setOpenMobile } = useSidebar()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const setLocale = useLanguageStore((state) => state.setLocale)
  const clearSandbox = useSandboxStore((state) => state.clearSandbox)
  const clearManagedAccount = useManagedAccountStore(
    (state) => state.clearManagedAccount,
  )

  const availableAccounts = useContextStore(
    (state) => state.availableUserAccounts,
  )
  const selectedUserAccountId = useContextStore(
    (state) => state.selectedUserAccountId,
  )
  const setSelectedUserAccountId = useContextStore(
    (state) => state.setSelectedUserAccountId,
  )
  const setAccountContext = useContextStore(
    (state) => state.setUserAccountContext,
  )
  const accountContext = useContextStore((state) => state.userAccountContext)

  const currentTheme = useThemeStore((state) => state.theme)
  const isWhiteLabel = useWhiteLabelStore((state) => state.isWhiteLabel)
  const companyName = useWhiteLabelStore((state) => state.companyName)
  const rectangleLightLogoUrl = useWhiteLabelStore(
    (state) => state.rectangleLightLogoUrl,
  )
  const rectangleDarkLogoUrl = useWhiteLabelStore(
    (state) => state.rectangleDarkLogoUrl,
  )
  const squareLightLogoUrl = useWhiteLabelStore(
    (state) => state.squareLightLogoUrl,
  )
  const squareDarkLogoUrl = useWhiteLabelStore(
    (state) => state.squareDarkLogoUrl,
  )
  const getUserContext = useUserAccountContextMutation()
  const activeAccount = availableAccounts.find(
    (account) => account.userAccountId === selectedUserAccountId,
  )

  if (!activeAccount) {
    return null
  }

  const isManaged = accountContext?.accountInformation.isManaged ?? false
  const hasActiveSubscription = accountContext?.hasActiveSubscription ?? false
  const showCreateButton = !isManaged && hasActiveSubscription

  const shouldShowDropdown = availableAccounts.length > 1 || showCreateButton

  const getAccountLogoUrl = (account: (typeof availableAccounts)[0]) => {
    return currentTheme === 'dark'
      ? account.squareDarkLogoUrl
      : account.squareLightLogoUrl
  }

  const accountTypeTranslations: Record<string, string> = {
    Company: t('accountType.Company'),
    Personal: t('accountType.Personal'),
  }

  const translateAccountType = (accountType: string) => {
    return accountTypeTranslations[accountType] ?? accountType
  }

  const filteredAccounts = availableAccounts.filter((account) =>
    account.accountName.toLowerCase().includes(search.toLowerCase()),
  )

  const handleAccountSwitch = async (userAccountId: string) => {
    setOpen(false)
    setOpenMobile(false)

    if (userAccountId === selectedUserAccountId) {
      return
    }

    try {
      setSelectedUserAccountId(userAccountId)

      // Reset sandbox mode and managed account when switching accounts
      clearSandbox()
      clearManagedAccount()

      const response = await getUserContext.mutateAsync()
      setAccountContext(response)

      const language = response.userAccountInformation.language
      if (language === 'PtBr') {
        setLocale('ptBr')
      } else {
        setLocale('enUs')
      }

      const theme = response.userAccountInformation.theme
      const root = window.document.documentElement
      root.classList.remove('light', 'dark')
      if (theme === 'Dark') {
        root.classList.add('dark')
        localStorage.setItem(LOCAL_STORAGE_KEYS.THEME, 'dark')
      } else {
        root.classList.add('light')
        localStorage.setItem(LOCAL_STORAGE_KEYS.THEME, 'light')
      }

      // Redirect to /vaults if inside a vault directory, otherwise to home
      const redirectUrl = getRedirectUrl()
      if (redirectUrl) {
        await navigate({ to: redirectUrl })
      } else {
        await navigate({ to: '/' })
      }
      window.location.reload()
    } catch (error) {
      logger.error(
        'Erro ao trocar de conta',
        error instanceof Error ? error : undefined,
        {
          userAccountId,
          selectedUserAccountId,
        },
      )
    }
  }

  const handleCreateAccount = () => {
    setOpen(false)
    setOpenMobile(false)
    onCreateAccount()
  }

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen)
    if (!newOpen) {
      setSearch('')
    }
  }

  const whiteLabelRectangleLogoUrl =
    currentTheme === 'dark' ? rectangleDarkLogoUrl : rectangleLightLogoUrl
  const whiteLabelSquareLogoUrl =
    currentTheme === 'dark' ? squareDarkLogoUrl : squareLightLogoUrl

  const renderBrandLogo = () => {
    if (!isWhiteLabel) {
      return <SignaterRectangleLogo height={28} />
    }

    if (whiteLabelRectangleLogoUrl) {
      return (
        <img
          src={whiteLabelRectangleLogoUrl}
          alt={companyName}
          className="h-7 w-auto max-w-full object-contain"
        />
      )
    }

    if (whiteLabelSquareLogoUrl) {
      return (
        <img
          src={whiteLabelSquareLogoUrl}
          alt={companyName}
          className="size-8 object-contain"
        />
      )
    }

    return <span className="truncate text-lg font-semibold">{companyName}</span>
  }

  const activeAccountSquareLogoUrl = getAccountLogoUrl(activeAccount)

  const headerContent = (
    <>
      {/* Directional swap: hide instantly on collapse, but only reveal after
          the sidebar width transition (200ms) settles on expand, so the
          rectangle logo is never seen squeezed mid-animation */}
      <div className="flex h-8 min-w-0 flex-1 items-center overflow-hidden opacity-100 transition-opacity delay-200 duration-150 group-data-[collapsible=icon]:opacity-0 group-data-[collapsible=icon]:transition-none">
        {renderBrandLogo()}
      </div>
      {/* Collapsed rail: the rectangle logo does not fit, show the square one */}
      <div
        className={cn(
          'hidden aspect-square size-8 shrink-0 items-center justify-center group-data-[collapsible=icon]:flex',
          !activeAccountSquareLogoUrl &&
            'bg-sidebar-primary text-sidebar-primary-foreground rounded-md',
        )}
      >
        {activeAccountSquareLogoUrl ? (
          <img
            src={activeAccountSquareLogoUrl}
            alt={activeAccount.accountName}
            className="size-8 object-contain"
          />
        ) : (
          <SignaterLogo size={32} className="size-8!" />
        )}
      </div>
      {shouldShowDropdown && (
        <IconSelector className="ml-auto size-4 shrink-0 group-data-[collapsible=icon]:hidden" />
      )}
    </>
  )

  const accountsPanel = (
    <>
      {/* Sticky header */}
      <div className="border-b p-2">
        <p
          id="account-switcher-title"
          className="text-muted-foreground px-1 pb-2 text-xs font-medium"
        >
          {t('accounts')}
        </p>
        <div className="relative">
          <IconSearch className="text-muted-foreground pointer-events-none absolute top-1/2 left-2 size-4 -translate-y-1/2" />
          <Input
            aria-label={t('searchPlaceholder')}
            placeholder={t('searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 pl-8"
          />
        </div>
      </div>

      {/* Scrollable account list. `scrollbar-soft` keeps the bar
          hidden until hover/scroll, matching the rest of the app. */}
      <div className="scrollbar-soft max-h-60 overflow-y-auto p-1">
        {filteredAccounts.length > 0 ? (
          filteredAccounts.map((account) => (
            <button
              key={account.userAccountId}
              onClick={() => handleAccountSwitch(account.userAccountId)}
              className="hover:bg-accent flex w-full cursor-pointer items-center gap-2 rounded-sm p-2 text-left"
            >
              <AccountLogo account={account} size={24} />
              <div className="flex flex-col">
                <span className="text-sm font-medium">
                  {account.accountName}
                </span>
                <span className="text-muted-foreground text-xs">
                  {translateAccountType(account.accountType)}
                </span>
              </div>
            </button>
          ))
        ) : (
          <p className="text-muted-foreground py-4 text-center text-sm">
            {t('noResults')}
          </p>
        )}
      </div>

      {/* Sticky footer */}
      {showCreateButton && (
        <div className="border-t p-1">
          <button
            onClick={handleCreateAccount}
            className="hover:bg-accent flex w-full cursor-pointer items-center gap-2 rounded-sm p-2"
          >
            <div className="bg-muted text-muted-foreground flex size-6 shrink-0 items-center justify-center rounded-md border">
              <CreateAccountIcon size={14} aria-hidden="true" />
            </div>
            <span className="text-muted-foreground text-sm font-medium">
              {t('createAccount')}
            </span>
          </button>
        </div>
      )}
    </>
  )

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        {shouldShowDropdown && isMobile ? (
          <>
            <SidebarMenuButton
              size="lg"
              aria-label={t('switchAccount')}
              className={cn(headerContainerClasses, 'cursor-pointer')}
              onClick={() => setOpen(true)}
            >
              {headerContent}
            </SidebarMenuButton>
            <ResponsiveDialog
              open={open}
              onOpenChange={handleOpenChange}
              surface="drawer"
            >
              <ResponsiveDialogContent>
                <ResponsiveDialogHeader visuallyHidden>
                  <ResponsiveDialogTitle>{t('accounts')}</ResponsiveDialogTitle>
                  <ResponsiveDialogDescription>
                    {t('switchAccount')}
                  </ResponsiveDialogDescription>
                </ResponsiveDialogHeader>
                <ResponsiveDialogBody flush>
                  {accountsPanel}
                </ResponsiveDialogBody>
              </ResponsiveDialogContent>
            </ResponsiveDialog>
          </>
        ) : shouldShowDropdown ? (
          <Popover open={open} onOpenChange={handleOpenChange}>
            <PopoverTrigger
              render={
                <SidebarMenuButton
                  size="lg"
                  aria-label={t('switchAccount')}
                  className={cn(
                    headerContainerClasses,
                    'data-popup-open:bg-sidebar-accent data-popup-open:text-sidebar-accent-foreground cursor-pointer',
                  )}
                />
              }
            >
              {headerContent}
            </PopoverTrigger>
            <PopoverContent
              aria-labelledby="account-switcher-title"
              className="w-(--anchor-width) min-w-56 gap-0 rounded-md p-0"
              align="start"
              side="right"
              sideOffset={4}
            >
              {accountsPanel}
            </PopoverContent>
          </Popover>
        ) : (
          <div
            data-testid="account-switcher-header"
            className={headerContainerClasses}
          >
            {headerContent}
          </div>
        )}
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
